import { useEffect, useState, useRef } from "react";
import { Routes, Route, Link, Navigate } from "react-router-dom";
import "@/App.css";
import { motion, useScroll, useTransform, AnimatePresence, useInView } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import { SEOHead } from "@/components/SEOHead";
import BlogPage from "@/pages/BlogPage";
import ArticlePage from "@/pages/ArticlePage";
import ServicePage from "@/pages/ServicePage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { 
  MessageCircle, ArrowRight, Send, X, 
  Shield, Zap, CheckCircle, 
  AlertTriangle, TrendingUp, Building2, ChevronRight,
  Phone, Mail, Landmark, Heart, Cpu,
  Users, Briefcase, UserCheck, CircleDot,
  Eye, Megaphone, MessageSquareText, Film,
  Wallet, Search, Clock, CalendarCheck
} from "lucide-react";
import axios from "axios";
import Header from "@/components/Header";
import { PortfolioSection } from "@/components/PortfolioSection";
import { VideoReelModal } from "@/components/VideoReelModal";
import { VideoShowcase } from "@/components/VideoShowcase";
import { ScheduleSection } from "@/components/ScheduleSection";
import { TransitionSpacer } from "@/components/TransitionSpacer";
import { QuoteSection } from "@/components/QuoteSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════════
// ANIMATED COUNTER HOOK
// ═══════════════════════════════════════════════════════
const useCounter = (target, suffix = "", inView) => {
  const [display, setDisplay] = useState("0");
  const num = parseFloat(target);
  
  useEffect(() => {
    if (!inView || isNaN(num)) { setDisplay(target); return; }
    let frame;
    let start = 0;
    const duration = 1800;
    const startTime = performance.now();
    
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = num * ease;
      
      if (Number.isInteger(num)) {
        setDisplay(Math.round(current) + suffix);
      } else {
        setDisplay(current.toFixed(1) + suffix);
      }
      
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [inView, num, suffix, target]);
  
  return display;
};

// Header imported from @/components/Header

// ═══════════════════════════════════════════════════════
// HERO SECTION - Sticky with deep parallax
// ═══════════════════════════════════════════════════════
const HeroSection = () => {
  const ref = useRef(null);
  const [isMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.92]);
  const textY = useTransform(scrollYProgress, [0, 0.5], ["0%", "15%"]);

  // Faster delays on mobile so content appears quicker
  const d = isMobile ? 0.1 : 0.3;

  return (
    <section ref={ref} className="relative h-[180vh]" data-testid="hero-section">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        {/* Deep morphing bg layers — hidden on mobile via CSS */}
        <div className="morph-bg w-[700px] h-[700px] bg-purple-700/15 top-[-15%] left-[-15%]" />
        <div className="morph-bg w-[400px] h-[400px] bg-violet-800/10 bottom-[-10%] right-[5%]" style={{ animationDelay: "-8s" }} />
        <div className="morph-bg w-[300px] h-[300px] bg-purple-900/10 top-[30%] right-[20%]" style={{ animationDelay: "-15s" }} />
        
        <motion.div style={{ y: textY, opacity, scale }} className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex flex-col items-center text-center">
            {/* Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: d, duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-10"
            >
              <span className="pulse-dot" />
              <span className="text-[13px] text-white/40 font-space tracking-widest uppercase">Comunicación Estratégica</span>
            </motion.div>

            {/* Headline with staggered reveal - CENTERED & LARGER */}
            <div className="relative">
              {/* Animated orbital glow background */}
              <div className="hero-signal-container">
                <div className="hero-orbit hero-orbit-1" />
                <div className="hero-orbit hero-orbit-2" />
                <div className="hero-orbit hero-orbit-3" />
                <div className="hero-glow-core" />
                <div className="hero-particle hero-particle-1" />
                <div className="hero-particle hero-particle-2" />
                <div className="hero-particle hero-particle-3" />
              </div>
              <div className="overflow-hidden mb-2">
                <motion.h1 
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: d + 0.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="font-space font-bold text-[clamp(3.5rem,11vw,13rem)] text-white leading-[0.85] tracking-[-0.04em] relative z-10"
                >
                  Lead The
                </motion.h1>
              </div>
              <div className="overflow-hidden mb-12">
                <motion.h1 
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: d + 0.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="font-space font-bold text-[clamp(3.5rem,11vw,13rem)] leading-[0.85] tracking-[-0.04em] relative z-10"
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500">Noise</span>
                </motion.h1>
              </div>
            </div>

            {/* Subtext */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: d + 0.5, duration: 0.6 }}
              className="text-base md:text-xl text-white/45 mb-14 max-w-lg leading-relaxed"
            >
              Protegemos y amplificamos la reputación de empresas que no pueden darse el lujo de improvisar.
            </motion.p>

            {/* CTAs - glassmorphism buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: d + 0.7 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <a href="#flow" className="btn-glass" data-testid="hero-cta-diagnostic">
                Evalúa tu situación
                <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#cases" className="btn-ghost" data-testid="hero-cta-cases">
                Ver resultados
              </a>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: d + 1.2 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-5 h-8 border border-white/15 rounded-full flex justify-center pt-2"
            >
              <motion.div 
                animate={{ opacity: [0.8, 0.2, 0.8], y: [0, 10, 0] }}
                transition={{ duration: 2.5, repeat: Infinity }}
                className="w-0.5 h-0.5 bg-white/40 rounded-full"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════
// DIAGNOSTIC FLOW - Expanded 7-step interactive quiz
// ═══════════════════════════════════════════════════════
const diagnosticSteps = [
  {
    question: "¿Qué describe mejor tu situación actual?",
    options: [
      { id: "crisis", icon: AlertTriangle, label: "Tenemos una crisis activa", desc: "Algo urgente está pasando ahora" },
      { id: "prevention", icon: Shield, label: "Queremos prevenir crisis", desc: "Buscamos protección proactiva" },
      { id: "growth", icon: TrendingUp, label: "Buscamos posicionamiento", desc: "Queremos crecer nuestra visibilidad" },
      { id: "rebrand", icon: Building2, label: "Necesitamos reinventarnos", desc: "Nuestra imagen necesita actualización" },
    ]
  },
  {
    question: "¿En qué sector opera tu organización?",
    options: [
      { id: "corporate", icon: Building2, label: "Corporativo / Empresarial", desc: "Industria, manufactura, servicios" },
      { id: "government", icon: Landmark, label: "Gobierno / Institucional", desc: "Sector público, municipalidades" },
      { id: "ngo", icon: Heart, label: "ONG / Fundación", desc: "Sin fines de lucro, cooperación" },
      { id: "tech", icon: Cpu, label: "Tecnología / Startups", desc: "Innovación, digital, fintech" },
    ]
  },
  {
    question: "¿Cuál es el tamaño de tu organización?",
    options: [
      { id: "startup", icon: CircleDot, label: "Startup / PYME", desc: "Menos de 50 empleados" },
      { id: "medium", icon: Users, label: "Empresa mediana", desc: "50–500 empleados" },
      { id: "large", icon: Building2, label: "Corporación", desc: "Más de 500 empleados" },
      { id: "institution", icon: Landmark, label: "Institución / ONG", desc: "Sector público o sin fines de lucro" },
    ]
  },
  {
    question: "¿Cómo manejan su comunicación actualmente?",
    options: [
      { id: "internal", icon: Users, label: "Equipo interno", desc: "Tenemos departamento de comunicación" },
      { id: "agency", icon: Briefcase, label: "Agencia externa", desc: "Trabajamos con otra agencia" },
      { id: "freelance", icon: UserCheck, label: "Freelancers / Ad-hoc", desc: "Contratamos según necesidad" },
      { id: "none", icon: Search, label: "Sin estrategia formal", desc: "No tenemos estructura definida" },
    ]
  },
  {
    question: "¿Cuál es tu principal preocupación?",
    options: [
      { id: "reputation", icon: Eye, label: "Proteger nuestra reputación", desc: "Monitoreo, prevención, narrativa" },
      { id: "visibility", icon: Megaphone, label: "Aumentar visibilidad", desc: "Prensa, medios, posicionamiento" },
      { id: "internal_comms", icon: MessageSquareText, label: "Comunicación interna", desc: "Cultura, engagement, alineación" },
      { id: "content", icon: Film, label: "Producción de contenido", desc: "Video, branding, materiales" },
    ]
  },
  {
    question: "¿Tienes presupuesto asignado para esto?",
    options: [
      { id: "yes_ready", icon: Wallet, label: "Sí, listo para invertir", desc: "Presupuesto definido y aprobado" },
      { id: "evaluating", icon: Search, label: "Estamos evaluando opciones", desc: "Cotizando propuestas" },
      { id: "need_proposal", icon: Briefcase, label: "Necesito propuesta primero", desc: "Para solicitar presupuesto interno" },
      { id: "exploring", icon: Eye, label: "Aún sin presupuesto", desc: "En fase de exploración" },
    ]
  },
  {
    question: "¿Qué tan pronto necesitas resultados?",
    options: [
      { id: "urgent", icon: AlertTriangle, label: "Inmediato", desc: "Las próximas 24–72 horas" },
      { id: "soon", icon: Clock, label: "Esta semana", desc: "En los próximos 7 días" },
      { id: "month", icon: CalendarCheck, label: "Este mes", desc: "En las próximas 4 semanas" },
      { id: "planning", icon: TrendingUp, label: "Estoy planificando", desc: "Sin urgencia específica" },
    ]
  }
];

const DiagnosticFlow = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const sectionRef = useRef(null);

  const handleSelect = (optionId) => {
    const newAnswers = { ...answers, [step]: optionId };
    setAnswers(newAnswers);
    
    if (step < diagnosticSteps.length - 1) {
      setTimeout(() => setStep(step + 1), 350);
    } else {
      setTimeout(() => {
        setShowResults(true);
        if (onComplete) onComplete(newAnswers);
        // Send diagnostic to Make.com webhook
        const rec = getDiagnosticRecommendation(newAnswers);
        axios.post(`${API}/api/diagnostic`, {
          answers: newAnswers,
          recommendation: rec.title,
          service: rec.service,
          urgent: rec.urgent || false,
        }).catch(() => {});
      }, 350);
    }
  };

  const getDiagnosticRecommendation = (ans) => {
    const situation = ans[0];
    const urgency = ans[6];
    if (situation === "crisis" || urgency === "immediate") {
      return { title: "Contención Inmediata", service: "Gestión de Crisis 360°", urgent: true };
    } else if (situation === "prevention") {
      return { title: "Protección Reputacional", service: "Reputación & Comunicación" };
    } else if (situation === "growth") {
      return { title: "Estrategia de Posicionamiento", service: "PR & Media Influence" };
    }
    return { title: "Transformación de Marca", service: "Contenido & Producción" };
  };

  const getRecommendation = () => {
    const situation = answers[0];
    const urgency = answers[6];

    if (situation === "crisis" || urgency === "urgent") {
      return {
        title: "Respuesta de Crisis Inmediata",
        desc: "Tu situación requiere atención prioritaria. Nuestro equipo está listo para activar el War Room.",
        service: "Crisis 360°",
        cta: "Hablar ahora",
        urgent: true
      };
    } else if (situation === "prevention") {
      return {
        title: "Protección Reputacional",
        desc: "Te ayudaremos a construir protocolos y sistemas de monitoreo para prevenir crisis.",
        service: "Reputación & Comunicación",
        cta: "Agendar diagnóstico"
      };
    } else if (situation === "growth") {
      return {
        title: "Estrategia de Posicionamiento",
        desc: "Amplificaremos tu voz con PR estratégico y cobertura mediática de alto impacto.",
        service: "PR & Media Influence",
        cta: "Explorar opciones"
      };
    } else {
      return {
        title: "Transformación de Marca",
        desc: "Rediseñaremos tu narrativa corporativa y produciremos contenido de autoridad.",
        service: "Contenido & Producción",
        cta: "Iniciar proceso"
      };
    }
  };

  const recommendation = showResults ? getRecommendation() : null;

  return (
    <section id="flow" ref={sectionRef} className="relative py-32 min-h-screen" data-testid="diagnostic-section">
      <div className="morph-bg w-[400px] h-[400px] bg-purple-600/8 top-[20%] right-[-5%]" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6">
        {/* Step indicator */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <span className="text-[13px] text-white/40 font-space tracking-wide">
              {showResults ? "Tu diagnóstico" : `Paso ${step + 1} de ${diagnosticSteps.length}`}
            </span>
            <div className="step-dots">
              {diagnosticSteps.map((_, i) => (
                <div 
                  key={i} 
                  className={`step-dot ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
                />
              ))}
            </div>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: showResults ? "100%" : `${((step + 1) / diagnosticSteps.length) * 100}%` }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!showResults ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
              <h2 className="font-space font-bold text-3xl md:text-5xl text-white mb-14 leading-tight" data-testid="diagnostic-question">
                {diagnosticSteps[step].question}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {diagnosticSteps[step].options.map((option, i) => (
                  <motion.button
                    key={option.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08 }}
                    onClick={() => handleSelect(option.id)}
                    className={`flow-card p-6 text-left ${answers[step] === option.id ? 'selected' : ''}`}
                    data-testid={`diagnostic-option-${option.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {option.icon && (
                        <div className="service-icon flex-shrink-0">
                          <option.icon className="w-5 h-5 text-purple-400/40" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-space font-semibold text-white text-base mb-1">{option.label}</h3>
                        <p className="text-white/40 text-sm">{option.desc}</p>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <ChevronRight className="w-4 h-4 text-white/15" />
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
              data-testid="diagnostic-result"
            >
              {recommendation.urgent && (
                <div className="inline-flex items-center gap-2 bg-red-500/8 border border-red-500/20 rounded-full px-4 py-2 mb-8">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400 font-space">Prioridad Alta</span>
                </div>
              )}

              <h2 className="font-space font-bold text-4xl md:text-6xl text-white mb-6 leading-tight">
                {recommendation.title}
              </h2>
              
              <p className="text-lg text-white/50 mb-6 max-w-2xl mx-auto leading-relaxed">
                {recommendation.desc}
              </p>

              <div className="inline-flex items-center gap-2 bg-purple-500/8 border border-purple-500/20 rounded-full px-5 py-2.5 mb-12">
                <span className="text-sm text-purple-400/70 font-space">Servicio recomendado: {recommendation.service}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#schedule" className="btn-primary text-base px-8 py-4" data-testid="diagnostic-result-cta">
                  Agendar reunión
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#contact" className="btn-ghost text-base px-8 py-4">
                  {recommendation.cta}
                </a>
                <button 
                  onClick={() => { setStep(0); setAnswers({}); setShowResults(false); }}
                  className="btn-secondary"
                  data-testid="diagnostic-restart"
                >
                  Volver a empezar
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════
// CUSTOM ECOSYSTEM ICONS
// ═══════════════════════════════════════════════════════
const IconRadar = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <circle cx="12" cy="12" r="10" strokeWidth="1" opacity="0.3" />
    <circle cx="12" cy="12" r="6" strokeWidth="1.5" opacity="0.5" />
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    <line x1="12" y1="12" x2="19" y2="5" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconHexShield = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <path d="M12 2L22 8V16L12 22L2 16V8L12 2Z" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M12 8V13" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const IconBroadcast = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <circle cx="12" cy="18" r="2" strokeWidth="1.5" />
    <path d="M12 16V8" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 11C9.3 9.5 10.5 9 12 9C13.5 9 14.7 9.5 16 11" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M5 8C7.5 5 9.5 4 12 4C14.5 4 16.5 5 19 8" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconLens = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="4" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    <line x1="12" y1="2" x2="12" y2="8" strokeWidth="1" opacity="0.3" />
    <line x1="12" y1="16" x2="12" y2="22" strokeWidth="1" opacity="0.3" />
    <line x1="2" y1="12" x2="8" y2="12" strokeWidth="1" opacity="0.3" />
    <line x1="16" y1="12" x2="22" y2="12" strokeWidth="1" opacity="0.3" />
  </svg>
);

// ═══════════════════════════════════════════════════════
// SERVICES - Horizontal scroll pinned section
// ═══════════════════════════════════════════════════════
const services = [
  { 
    icon: IconRadar, 
    num: "01",
    title: "Reputación &\nComunicación", 
    desc: "Estrategia institucional, narrativa corporativa y dashboards de reputación en tiempo real.",
    features: ["Estrategia institucional", "Narrativa corporativa", "Comunicación interna", "Monitoreo 24/7"],
    slug: "reputacion-corporativa",
  },
  { 
    icon: IconHexShield, 
    num: "02",
    title: "Gestión de\nCrisis 360°", 
    desc: "War room 24/7, respuesta inmediata, vocería de crisis y reconstrucción reputacional.",
    features: ["War Room 24/7", "Respuesta inmediata", "Vocería de crisis", "Post-crisis"],
    slug: "gestion-de-crisis",
  },
  { 
    icon: IconBroadcast, 
    num: "03",
    title: "PR & Media\nInfluence", 
    desc: "Relaciones públicas, media training y cobertura mediática de alto impacto.",
    features: ["Media training", "Agenda periodística", "Lanzamientos", "Earned media"],
    slug: "relaciones-publicas",
  },
  { 
    icon: IconLens, 
    num: "04",
    title: "Contenido &\nProducción", 
    desc: "Producción audiovisual institucional, investor decks y eventos corporativos.",
    features: ["Producción audiovisual", "Investor decks", "Eventos", "Branding"],
    slug: "contenido-produccion",
  },
];

const ServicesSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // 4 panels: we need to scroll through 3 panel-widths (first is already visible)
  const x = useTransform(scrollYProgress, [0.05, 0.95], ["0%", "-75%"]);

  return (
    <section id="services" ref={containerRef} className="relative h-[400vh]" data-testid="services-section">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        {/* Section label */}
        <div className="max-w-7xl mx-auto px-6 w-full mb-12">
          <motion.div className="flex items-center gap-3">
            <span className="pulse-dot" />
            <span className="text-[13px] text-white/35 font-space tracking-widest uppercase">Servicios</span>
          </motion.div>
        </div>

        {/* Horizontal scroll track */}
        <motion.div style={{ x, width: "max-content" }} className="flex will-change-transform">
          {services.map((service, i) => (
            <div key={i} className="service-panel flex-shrink-0">
              <motion.div 
                className="service-glass-card"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                {/* Animated glow border */}
                <div className="service-glow-border" />
                <div className="service-glass-inner">
                  {/* Large faded number */}
                  <span className="service-number">{service.num}</span>
                  
                  <div className="service-icon mb-8">
                    <service.icon className="w-5 h-5 text-purple-400/70" />
                  </div>
                  
                  <h3 className="font-space font-bold text-2xl sm:text-4xl md:text-6xl text-white mb-6 leading-tight whitespace-pre-line">
                    {service.title}
                  </h3>
                  <p className="text-base md:text-lg text-white/45 mb-10 max-w-md leading-relaxed">{service.desc}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((feature, j) => (
                      <span 
                        key={j} 
                        className="text-xs px-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-full text-white/45 backdrop-blur-sm"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  <Link to={`/servicios/${service.slug}`} className="inline-flex items-center gap-2 mt-8 text-sm font-space text-purple-400/60 hover:text-purple-300 transition-colors" data-testid={`service-link-${service.num}`}>
                    Explorar servicio <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════
// CASE CARD with animated counter
// ═══════════════════════════════════════════════════════
const CaseCard = ({ item, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  
  const roiNum = parseFloat(item.roi);
  const roiDisplay = useCounter(roiNum, "x", isInView);
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.6 }}
      className="glass-card p-10 group"
      data-testid={`case-card-${item.client.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="text-xs text-purple-400/40 font-space tracking-wide">{item.type}</span>
      <h3 className="font-space font-semibold text-xl text-white mt-2 mb-10">{item.client}</h3>
      
      <div className="grid grid-cols-3 gap-6">
        <div>
          <span className="block font-space font-bold text-3xl text-white number-counter">
            {isInView ? roiDisplay : "0x"}
          </span>
          <span className="text-xs text-white/30 mt-1 block">ROI</span>
        </div>
        <div>
          <span className="block font-space font-bold text-3xl text-white number-counter">{item.reach}</span>
          <span className="text-xs text-white/30 mt-1 block">Alcance</span>
        </div>
        <div>
          <span className="block font-space font-bold text-3xl text-white number-counter">{item.coverage}</span>
          <span className="text-xs text-white/30 mt-1 block">Positivo</span>
        </div>
      </div>
    </motion.div>
  );
};

const CasesSection = () => {
  const cases = [
    { client: "Ayuntamiento de Madrid", type: "Posicionamiento", roi: "7", reach: "2.5M", coverage: "95%" },
    { client: "Grupo ASIMEX", type: "Relaciones Institucionales", roi: "4.5", reach: "700K", coverage: "95%" },
    { client: "Fundación Paraíso Down", type: "Impacto Social", roi: "5", reach: "500K", coverage: "100%" },
  ];

  const clients = ["Hilton", "USAID", "Mitsubishi", "Colliers", "GIZ", "Huawei", "InterContinental", "EU Delegation"];

  return (
    <section id="cases" className="relative py-32" data-testid="cases-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="pulse-dot" />
            <span className="text-[13px] text-white/30 font-space tracking-widest uppercase">Casos</span>
          </div>
          <h2 className="font-space font-bold text-4xl md:text-6xl text-white leading-tight">
            Resultados<br />medibles
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-24">
          {cases.map((item, i) => (
            <CaseCard key={i} item={item} delay={i * 0.12} />
          ))}
        </div>

        {/* Clients marquee */}
        <div className="section-divider mb-16" />
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="overflow-hidden"
        >
          <p className="text-center text-[13px] text-white/20 mb-10 font-space tracking-widest uppercase">Confían en nosotros</p>
          <div className="relative overflow-hidden">
            <div className="clients-marquee">
              {[...clients, ...clients].map((client, i) => (
                <span key={i} className="font-space text-lg text-white/20 whitespace-nowrap">{client}</span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════
// CERTIFICATIONS STRIP
// ═══════════════════════════════════════════════════════
const CertificationsStrip = () => {
  const certs = [
    "ISO Certified",
    "CRI Crisis Management Specialist", 
    "PECB Crisis Manager",
    "+65 Proyectos Corporativos",
    "Cobertura Centroamérica & Europa"
  ];
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="py-16 border-y border-white/[0.04]"
      data-testid="certifications-strip"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-4">
          {certs.map((cert, i) => (
            <div key={i} className="flex items-center gap-3">
              <CheckCircle className="w-4 h-4 text-purple-500/30" />
              <span className="text-sm text-white/30 font-space">{cert}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════
// CONTACT SECTION
// ═══════════════════════════════════════════════════════
const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  return (
    <section id="contact" className="relative py-32" data-testid="contact-section">
      <div className="morph-bg w-[500px] h-[500px] bg-purple-700/8 bottom-0 left-[-10%]" />
      
      <div className="relative z-10 max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="pulse-dot" />
            <span className="text-[13px] text-white/30 font-space tracking-widest uppercase">Contacto</span>
          </div>
          <h2 className="font-space font-bold text-4xl md:text-6xl text-white mb-4 leading-tight">
            ¿Listo para<br />tomar control?
          </h2>
          <p className="text-white/30 text-lg">
            Agenda una llamada estratégica. Sin compromiso.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 md:p-12"
        >
          {submitted ? (
            <div className="text-center py-12" data-testid="contact-success">
              <div className="w-16 h-16 rounded-2xl bg-purple-500/8 border border-purple-500/15 backdrop-blur-md flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-purple-400/60" />
              </div>
              <h3 className="font-space font-semibold text-2xl text-white mb-2">Mensaje enviado</h3>
              <p className="text-white/35">Te contactamos en las próximas horas.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[13px] text-white/40 mb-2 font-space">Nombre</label>
                  <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="Tu nombre" data-testid="contact-name" />
                </div>
                <div>
                  <label className="block text-[13px] text-white/40 mb-2 font-space">Email</label>
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" placeholder="tu@email.com" data-testid="contact-email" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] text-white/40 mb-2 font-space">Empresa</label>
                <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input" placeholder="Nombre de tu empresa" data-testid="contact-company" />
              </div>
              <div>
                <label className="block text-[13px] text-white/40 mb-2 font-space">¿Qué está pasando?</label>
                <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input resize-none" placeholder="Cuéntanos tu situación..." data-testid="contact-message" />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full py-4" data-testid="contact-submit">
                {submitting ? "Enviando..." : "Enviar mensaje"}
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          )}
        </motion.div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mt-12">
          <a href="tel:+50364330431" className="flex items-center gap-2 text-white/30 hover:text-white/60 transition-colors">
            <Phone className="w-4 h-4" />
            <span className="text-sm">+503 6433 0431</span>
          </a>
          <a href="mailto:informacion@disruptiva.online" className="flex items-center gap-2 text-white/30 hover:text-white/60 transition-colors">
            <Mail className="w-4 h-4" />
            <span className="text-sm">informacion@disruptiva.online</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════
// CHAT MARKDOWN RENDERER
// ═══════════════════════════════════════════════════════
const ChatMarkdown = ({ text }) => {
  if (!text) return null;
  
  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Empty line = spacing
    if (!line.trim()) {
      elements.push(<div key={i} className="h-2" />);
      i++;
      continue;
    }
    
    // Headers
    if (line.startsWith("### ")) {
      elements.push(<h4 key={i} className="font-space font-semibold text-white text-sm mt-3 mb-1">{parseBold(line.slice(4))}</h4>);
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      elements.push(<h3 key={i} className="font-space font-semibold text-white text-base mt-3 mb-1">{parseBold(line.slice(3))}</h3>);
      i++;
      continue;
    }
    
    // Numbered list
    const numMatch = line.match(/^(\d+)[.)]\s+(.+)/);
    if (numMatch) {
      const items = [{ num: numMatch[1], text: numMatch[2] }];
      i++;
      while (i < lines.length) {
        const nextMatch = lines[i].match(/^(\d+)[.)]\s+(.+)/);
        if (nextMatch) {
          items.push({ num: nextMatch[1], text: nextMatch[2] });
          i++;
        } else break;
      }
      elements.push(
        <ol key={`ol-${i}`} className="space-y-1.5 my-2">
          {items.map((item, j) => (
            <li key={j} className="flex gap-2">
              <span className="text-purple-400 font-space font-semibold text-xs min-w-[18px] mt-0.5">{item.num}.</span>
              <span className="flex-1">{parseBold(item.text)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }
    
    // Bullet list
    if (line.match(/^[-•*]\s+/)) {
      const items = [line.replace(/^[-•*]\s+/, "")];
      i++;
      while (i < lines.length && lines[i].match(/^[-•*]\s+/)) {
        items.push(lines[i].replace(/^[-•*]\s+/, ""));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-1.5 my-2">
          {items.map((item, j) => (
            <li key={j} className="flex gap-2">
              <span className="text-purple-400 mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-400/60 shrink-0" />
              <span className="flex-1">{parseBold(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }
    
    // Regular paragraph
    elements.push(<p key={i} className="my-1">{parseBold(line)}</p>);
    i++;
  }
  
  return <div className="chat-formatted">{elements}</div>;
};

// Parse **bold** and *italic* inline
function parseBold(text) {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;
  
  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const before = remaining.slice(0, boldMatch.index);
      if (before) parts.push(<span key={key++}>{before}</span>);
      parts.push(<strong key={key++} className="text-white font-semibold">{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }
    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }
  
  return parts.length === 1 ? parts[0] : parts;
}

// ═══════════════════════════════════════════════════════
// CHATBOT
// ═══════════════════════════════════════════════════════
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hola, gracias por contactar a Disruptiva. Cuéntame, ¿qué está pasando con tu empresa o marca?", time: new Date() }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const checkMobile = () => window.matchMedia("(max-width: 768px)").matches;
    if (checkMobile()) return;
    let opened = false;
    const handleScroll = () => {
      if (opened || checkMobile()) return;
      const el = document.getElementById("services");
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < window.innerHeight * 0.5) {
        opened = true;
        setIsOpen(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg, time: new Date() }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: msg, session_id: sessionId });
      setMessages(m => [...m, { role: "assistant", content: res.data.response, time: new Date() }]);
    } catch { 
      setMessages(m => [...m, { role: "assistant", content: "Error. Contacta al +503 64330431.", time: new Date() }]); 
    }
    setLoading(false);
  };

  const fmtTime = (d) => {
    if (!d) return "";
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  return (
    <>
      <motion.button 
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.5, type: "spring" }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2.5 h-12 pl-1.5 pr-5 rounded-full bg-[#0c0c0c] border border-purple-500/30 text-white hover:border-purple-400/60 transition-all duration-300 shadow-lg shadow-purple-900/25"
        data-testid="chatbot-toggle"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shrink-0">
          {isOpen ? <X className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
        </div>
        <span className="text-xs font-space font-semibold tracking-wide text-white/80">{isOpen ? "Cerrar" : "Chat IA"}</span>
      </motion.button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.95 }} 
            animate={{ opacity: 1, y: 0, scale: 1 }} 
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-24 left-6 z-50 w-[380px] max-md:inset-0 max-md:bottom-0 max-md:left-0 max-md:w-full max-md:h-full max-md:rounded-none max-md:z-[60] bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl shadow-purple-900/10 flex flex-col"
            data-testid="chatbot-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-900/40 to-purple-800/20 px-4 py-3 flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-[#0a0a0a]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-space font-semibold text-white text-sm">Asistente Disruptiva</h3>
                <p className="text-xs text-green-400/80">{loading ? "escribiendo..." : "en linea"}</p>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-2 rounded-full text-white/40 hover:text-white hover:bg-white/[0.05] transition-all" data-testid="chatbot-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Messages */}
            <div className="h-96 max-md:flex-1 overflow-y-auto px-3 py-3 space-y-1 chatbot-messages-bg" data-testid="chatbot-messages">
              <div className="flex justify-center mb-3">
                <span className="bg-white/[0.06] text-white/40 text-[10px] font-space px-3 py-1 rounded-full">Hoy</span>
              </div>
              
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} mb-1`}>
                  <div className={`relative max-w-[82%] px-3 pt-2 pb-1 text-[13px] leading-relaxed ${
                    m.role === "user" 
                      ? "bg-purple-600/20 border border-purple-500/15 rounded-2xl rounded-br-md text-white" 
                      : "bg-white/[0.05] border border-white/[0.06] rounded-2xl rounded-bl-md text-white/75"
                  }`}>
                    <div className="pb-4">
                      {m.role === "user" ? m.content : <ChatMarkdown text={m.content} />}
                    </div>
                    <div className="absolute bottom-1.5 right-3 flex items-center gap-1">
                      <span className="text-[10px] text-white/25">{fmtTime(m.time)}</span>
                      {m.role === "user" && (
                        <svg className="w-3.5 h-3.5 text-purple-400/60" viewBox="0 0 16 16" fill="none">
                          <path d="M1.5 8.5L5 12L14.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M5 8.5L8.5 12L14.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start mb-1">
                  <div className="bg-white/[0.05] border border-white/[0.06] px-4 py-3 rounded-2xl rounded-bl-md">
                    <div className="flex items-center gap-1">
                      {[0, 0.2, 0.4].map((d, i) => (
                        <span key={i} className="w-2 h-2 bg-purple-400/50 rounded-full animate-bounce" style={{ animationDelay: `${d}s`, animationDuration: "0.8s" }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input bar */}
            <div className="px-3 py-3 bg-[#0c0c0c] border-t border-white/[0.06]">
              <div className="flex items-end gap-2">
                <div className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-2xl overflow-hidden">
                  <input 
                    type="text" 
                    value={input} 
                    onChange={(e) => setInput(e.target.value)} 
                    onKeyPress={(e) => e.key === "Enter" && send()} 
                    placeholder="Escribe un mensaje" 
                    className="w-full bg-transparent px-4 py-2.5 text-[13px] text-white focus:outline-none placeholder-white/25"
                    data-testid="chatbot-input"
                  />
                </div>
                <button 
                  onClick={send} 
                  disabled={!input.trim() || loading}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center hover:from-purple-500 hover:to-purple-600 transition-all disabled:opacity-30 shrink-0"
                  data-testid="chatbot-send"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ═══════════════════════════════════════════════════════
// WHATSAPP
// ═══════════════════════════════════════════════════════
const WhatsApp = () => (
  <motion.a 
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ delay: 1.7, type: "spring" }}
    href="https://wa.me/50364330431" 
    target="_blank" 
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-2xl bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:shadow-[#25D366]/30 transition-shadow"
    data-testid="whatsapp-button"
  >
    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </motion.a>
);

// ═══════════════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════════════
const SOCIAL_LINKS = [
  { name: "Instagram", url: "https://www.instagram.com/agenciadisruptiva_/", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
  { name: "Facebook", url: "https://www.facebook.com/AgenciaDisruptivaComunicacion/", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
  { name: "LinkedIn", url: "https://www.linkedin.com/company/disruptiva-comunicaci%C3%B3n/", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
];

const CERTIFICATIONS = [
  { name: "Claude Partner Network", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_disruptiva-redesign/artifacts/ye5gj85g_claude_partner_network.png" },
  { name: "CMSB", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_disruptiva-redesign/artifacts/idf1k4i5_CMSB_logo_principal_color.PNG" },
  { name: "IRL - US Embassy", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_disruptiva-redesign/artifacts/frg5xu1n_IRL%20-%20Blanco.png" },
];

const PARTNERSHIPS = [
  "ISO 22361 · Gestión de Crisis",
  "PECB · Crisis Manager",
  "CRI · Crisis Management Specialist",
  "SNBX · Aliados",
  "Impact Hub San Salvador",
];

const Footer = () => (
  <footer className="py-20 border-t border-white/[0.04]" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      {/* Certifications & Partnerships */}
      <div className="mb-16 pb-12 border-b border-white/[0.04]" data-testid="footer-certifications">
        <span className="text-[11px] text-white/25 font-space tracking-[0.2em] uppercase block text-center mb-8">
          Certificaciones · Membresías · Alianzas
        </span>
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 mb-8">
          {CERTIFICATIONS.map((cert) => (
            <div key={cert.name} className="flex items-center justify-center" title={cert.name}>
              <img
                src={cert.img}
                alt={cert.name}
                className="h-12 md:h-14 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
                loading="lazy"
              />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {PARTNERSHIPS.map((p) => (
            <span key={p} className="text-[11px] text-white/20 font-space tracking-wider">{p}</span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
        {/* Brand */}
        <div className="md:col-span-1">
          <img src="https://customer-assets.emergentagent.com/job_5035be6a-7db4-4e98-9a71-35ab08f9d865/artifacts/z23j60x7_1.svg" alt="Disruptiva" className="h-7 w-auto opacity-50 mb-4" />
          <p className="text-sm text-white/30 leading-relaxed mb-5">Comunicación estratégica que transforma la percepción de tu marca.</p>
          {/* Social links */}
          <div className="flex items-center gap-3">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-white/30 hover:text-white/70 hover:border-white/[0.15] transition-all"
                aria-label={social.name}
                data-testid={`social-${social.name.toLowerCase()}`}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
        {/* Services */}
        <div>
          <h4 className="font-space font-semibold text-white/50 text-xs tracking-widest uppercase mb-4">Servicios</h4>
          <div className="flex flex-col gap-2.5">
            <Link to="/servicios/reputacion-corporativa" className="text-sm text-white/30 hover:text-white/60 transition-colors">Reputación Corporativa</Link>
            <Link to="/servicios/gestion-de-crisis" className="text-sm text-white/30 hover:text-white/60 transition-colors">Gestión de Crisis</Link>
            <Link to="/servicios/relaciones-publicas" className="text-sm text-white/30 hover:text-white/60 transition-colors">Relaciones Públicas</Link>
            <Link to="/servicios/contenido-produccion" className="text-sm text-white/30 hover:text-white/60 transition-colors">Contenido & Producción</Link>
          </div>
        </div>
        {/* Resources */}
        <div>
          <h4 className="font-space font-semibold text-white/50 text-xs tracking-widest uppercase mb-4">Recursos</h4>
          <div className="flex flex-col gap-2.5">
            <Link to="/blog" className="text-sm text-white/30 hover:text-white/60 transition-colors">Blog</Link>
            <a href="#flow" className="text-sm text-white/30 hover:text-white/60 transition-colors">Diagnóstico gratuito</a>
            <a href="#cotizar" className="text-sm text-white/30 hover:text-white/60 transition-colors">Solicitar cotización</a>
            <a href="#portfolio" className="text-sm text-white/30 hover:text-white/60 transition-colors">Portafolio</a>
          </div>
        </div>
        {/* Contact */}
        <div>
          <h4 className="font-space font-semibold text-white/50 text-xs tracking-widest uppercase mb-4">Contacto</h4>
          <div className="flex flex-col gap-2.5">
            <a href="mailto:informacion@disruptiva.online" className="text-sm text-white/30 hover:text-white/60 transition-colors">informacion@disruptiva.online</a>
            <a href="tel:+50364330431" className="text-sm text-white/30 hover:text-white/60 transition-colors">+503 6433 0431</a>
            <Link to="/politica-de-privacidad" className="text-sm text-white/30 hover:text-white/60 transition-colors" data-testid="footer-privacy">Política de Privacidad</Link>
            <Link to="/terminos-y-condiciones" className="text-sm text-white/30 hover:text-white/60 transition-colors" data-testid="footer-terms">Términos y Condiciones</Link>
          </div>
        </div>
      </div>
      <div className="pt-8 border-t border-white/[0.04] flex flex-col md:flex-row items-center justify-between gap-4">
        <span className="text-xs text-white/20 font-space">© 2026 Disruptiva. Todos los derechos reservados.</span>
        <span className="text-xs text-white/20 font-space">El Salvador · Centroamérica</span>
      </div>
    </div>
  </footer>
);

// ═══════════════════════════════════════════════════════
// HOMEPAGE
// ═══════════════════════════════════════════════════════
function HomePage() {
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [reelOpen, setReelOpen] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  return (
    <div className="min-h-screen bg-black" data-testid="app-root">
      <SEOHead />
      <div className="grain-overlay" />
      <Header />
      <main>
        <HeroSection />
        <div className="section-divider" />
        <ServicesSection />
        <div className="section-divider" />
        <DiagnosticFlow onComplete={setDiagnosticResult} />
        <TransitionSpacer />
        <PortfolioSection onOpenReel={() => setReelOpen(true)} />
        <VideoShowcase />
        <div className="section-divider" />
        <CasesSection />
        <CertificationsStrip />
        <ScheduleSection />
        <QuoteSection />
        <ContactSection />
      </main>
      <Footer />
      <Chatbot />
      <WhatsApp />
      <VideoReelModal isOpen={reelOpen} onClose={() => setReelOpen(false)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN APP WITH ROUTES
// ═══════════════════════════════════════════════════════
function App() {
  const [adminUser, setAdminUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("disruptiva_admin")); } catch { return null; }
  });

  const handleLogin = (user) => {
    setAdminUser(user);
    localStorage.setItem("disruptiva_admin", JSON.stringify(user));
  };

  const handleLogout = () => {
    setAdminUser(null);
    localStorage.removeItem("disruptiva_admin");
  };

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<ArticlePage />} />
      <Route path="/servicios/reputacion-corporativa" element={<ServicePage slug="reputacion-corporativa" />} />
      <Route path="/servicios/gestion-de-crisis" element={<ServicePage slug="gestion-de-crisis" />} />
      <Route path="/servicios/relaciones-publicas" element={<ServicePage slug="relaciones-publicas" />} />
      <Route path="/servicios/contenido-produccion" element={<ServicePage slug="contenido-produccion" />} />
      <Route path="/politica-de-privacidad" element={<PrivacyPage />} />
      <Route path="/terminos-y-condiciones" element={<TermsPage />} />
      <Route path="/admin/login" element={
        adminUser ? <Navigate to="/admin" replace /> : <AdminLogin onLogin={handleLogin} />
      } />
      <Route path="/admin" element={
        adminUser ? <AdminDashboard user={adminUser} onLogout={handleLogout} /> : <Navigate to="/admin/login" replace />
      } />
    </Routes>
  );
}

export default App;
